import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Chip, IconButton, Tooltip, Card, CardContent, Button } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import DownloadIcon from "@mui/icons-material/Download";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ReceiptIcon from "@mui/icons-material/Receipt";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { setModal } from "../../features/modal/ModalSlice";
import CustomButton from "../../components/Button/CustomButton";
import apiClient from "../../services/apiClient";

const Billing = () => {
  const dispatch = useDispatch();
  const modal = useSelector((state) => state.modal?.modal);
  const userId = useSelector((state) => state.userInfo?.user?.id);

  const [history, setHistory] = useState([]);

  const [usageBreakdown, setUsageBreakdown] = useState([]);

  const [loading, setLoading] = useState(false);
  const [billingData, setBillingData] = useState(null);

  const fetchBilling = async () => {
    if (!userId) return;

    try {
      setLoading(true);

      const res = await apiClient.get(`/billing/user/${userId}/summary`);

      if (!res) {
        console.error("Failed to load billing summary");
        return;
      }

      if (res.data) {
        const data = res.data;

        setBillingData({
          totalAmount: data.total || 0,
          analysis: data.analysis || 0,
          llm: data.llm || 0,
          stt: data.stt || 0,
          tools: data.tools || 0,
          tts: data.tts || 0,
        });

        const newUsageBreakdown = [
          {
            category: "Voice Calls",
            usage: "-",
            cost: data.total || 0,
          },
          {
            category: "Analysis",
            usage: "-",
            cost: data.analysis || 0,
          },
          {
            category: "AI Processing (LLM)",
            usage: "-",
            cost: data.llm || 0,
          },
          {
            category: "Text-to-Speech",
            usage: "-",
            cost: data.tts || 0,
          },
          {
            category: "Speech-to-Text",
            usage: "-",
            cost: data.stt || 0,
          },
          {
            category: "Tools",
            usage: "-",
            cost: data.tools || 0,
          },
        ];

        setUsageBreakdown(newUsageBreakdown);
      }
    } catch (err) {
      console.error("Error fetching billing summary:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    if (!userId) return;

    try {
      setLoading(true);

      const res = await apiClient.get(`/payment/history/${userId}`);

      if (!res) {
        console.error("Failed to load history");
        return;
      }

      setHistory(res.data || []);
    } catch (err) {
      console.error("Error fetching payment history:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchBilling();
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (modal?.isUpdatedIn === "BILLING") {
      fetchBilling();
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal]);

  const handleDownloadInvoice = (invoice) => {
    alert(`Downloading transaction ${invoice.razorpay_payment_id || invoice._id}...`);
  };

  const handleManagePayment = () => {
    dispatch(setModal({ type: "MANAGE_PAYMENT_METHOD" }));
  };

  const handleUpgradePlan = () => {
    dispatch(setModal({ type: "UPGRADE_PLAN" }));
  };

  const invoiceColumns = useMemo(
    () => [
      {
        accessorKey: "razorpay_payment_id",
        header: "Payment ID",
        size: 200,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ReceiptIcon sx={{ color: "primary.main" }} />
            <Typography variant="body2" fontWeight={600}>
              {row.original.razorpay_payment_id || "-"}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "description",
        header: "Description",
        size: 200,
      },
      {
        accessorKey: "created_at",
        header: "Date",
        size: 150,
        Cell: ({ cell }) => {
          const date = new Date(cell.getValue());
          return (
            <Typography variant="body2">
              {date.toLocaleDateString()}
            </Typography>
          );
        },
      },
      {
        accessorKey: "amount",
        header: "Amount",
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          let formattedValue;
          if (value >= 1) {
            formattedValue = value.toFixed(2);
          } else if (value >= 0.01) {
            formattedValue = value.toFixed(4);
          } else if (value >= 0.0001) {
            formattedValue = value.toFixed(6);
          } else {
            formattedValue = value.toFixed(8);
          }
          return (
            <Typography variant="body2" fontWeight={600} color="primary">
              ${formattedValue}
            </Typography>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 100,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue()}
            size="small"
            color={cell.getValue() === "success" ? "success" : "error"}
            sx={{ fontWeight: 500, textTransform: "capitalize" }}
          />
        ),
      },
    ],
    []
  );

  const usageColumns = useMemo(
    () => [
      {
        accessorKey: "category",
        header: "Category",
        size: 180,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={600}>
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "usage",
        header: "Usage",
        size: 180,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "cost",
        header: "Cost",
        size: 120,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          let formattedValue;
          if (value >= 1) {
            formattedValue = value.toFixed(2);
          } else if (value >= 0.01) {
            formattedValue = value.toFixed(4);
          } else if (value >= 0.0001) {
            formattedValue = value.toFixed(6);
          } else {
            formattedValue = value.toFixed(8);
          }
          return (
            <Typography variant="body2" fontWeight={600} color="primary">
              ${formattedValue}
            </Typography>
          );
        },
      },
    ],
    []
  );

  const currentPlan = "Professional";
  const monthlyBudget = 500.0;
  const currentSpend = billingData?.totalAmount || 0;
  const totalInvoices = history.length;
  const spendPercentage = (currentSpend / monthlyBudget) * 100;

  return (

    <>
    
    <div className="pageTitle1 d-flex justify-content-between align-items-center px-5 py-4">
      <div>
        <h3>Billing & Usage</h3>
        <p>Manage your subscription and track VAPI costs</p>
      </div>
      <Box sx={{ display: "flex", gap: 1 }}>
        <CustomButton
          icon={<CreditCardIcon />}
          iconPosition="left"
          text="Payment Method"
          variant="outlined"
          onClick={handleManagePayment}
        />
        <CustomButton
          icon={<TrendingUpIcon />}
          iconPosition="left"
          text="Upgrade Plan"
          onClick={handleUpgradePlan}
        />
      </Box>
    </div>
    
    <Box sx={{ padding: 3 }}>

      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <Card sx={{ flex: 1, borderRadius: 2, border: "1px solid #E0E0E0" }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Current Plan
            </Typography>
            <Typography variant="h4" fontWeight={700} color="primary">
              {currentPlan}
            </Typography>
            <Button size="small" sx={{ mt: 1 }}>
              View Details
            </Button>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, borderRadius: 2, border: "1px solid #E0E0E0" }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              This Month's Spend
            </Typography>
            <Typography variant="h4" fontWeight={700} color="warning.main">
              ${currentSpend >= 1 ? currentSpend.toFixed(2) : currentSpend >= 0.01 ? currentSpend.toFixed(4) : currentSpend >= 0.0001 ? currentSpend.toFixed(6) : currentSpend.toFixed(8)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              of ${monthlyBudget.toFixed(2)} budget ({spendPercentage.toFixed(4)}%)
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, borderRadius: 2, border: "1px solid #E0E0E0" }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Total Invoices
            </Typography>
            <Typography variant="h4" fontWeight={700} color="info.main">
              {totalInvoices}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              All paid on time
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          Current Month Usage
        </Typography>
        <MaterialReactTable
          columns={usageColumns}
          data={usageBreakdown}
          state={{ isLoading: loading }}
          enableColumnFilters={false}
          enableGlobalFilter={false}
          enableSorting={false}
          enablePagination={false}
          muiTablePaperProps={{
            elevation: 0,
            sx: {
              borderRadius: 2,
              border: "1px solid #E0E0E0",
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: "#F9FAFB",
              fontWeight: 600,
            },
          }}
        />
      </Box>

      <Box>
        <Typography variant="h6" gutterBottom fontWeight={600}>
          Billing History
        </Typography>
        <MaterialReactTable
          columns={invoiceColumns}
          data={history}
          state={{ isLoading: loading }}
          enableRowActions
          positionActionsColumn="last"
          renderRowActions={({ row }) => (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Tooltip title="Download Invoice">
                <IconButton
                  size="small"
                  onClick={() => handleDownloadInvoice(row.original)}
                  sx={{ color: "primary.main" }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          enableColumnFilters
          enableGlobalFilter
          enableSorting
          enablePagination={false}
          muiTableContainerProps={{
            sx: {
              maxHeight: "500px",
              overflowY: "auto",
            },
          }}
          muiTablePaperProps={{
            elevation: 0,
            sx: {
              borderRadius: 2,
              border: "1px solid #E0E0E0",
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: "#F9FAFB",
              fontWeight: 600,
              position: "sticky",
              top: 0,
              zIndex: 1,
            },
          }}
        />
      </Box>
    </Box>
    
    
    </>

  );
};

export default Billing;
